import React, { ReactElement, useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Snackbar, Alert, Box, Backdrop } from '@mui/material';
import { LoadingComponent } from 'ndr-designsystem';
import {
  AuthenticationInProgress,
  ProtectedRoute,
  SignInOidcFragment,
  SignOutOidcFragment,
  useAuthentication,
} from './authentication';
import { configureGeneratedClient } from './api/generatedClientConfig';
import Home from './pages/Home/Home';
import ForgetPassword from './pages/ForgetPassword/ForgetPassword';
import Forbidden from './pages/Forbidden/Forbidden';
import './App.css';
import buildTitle from './Components/titleHelper';
import ErrorBoundary from './Components/ErrorBoundary';
import { useAppDispatch, useAppSelector } from './app/hooks';
import {
  setMessageErrors,
  setNotificationAlertMessage,
  setUserRole,
} from './features/appStateSlice';
import { fetchCurrentUserRole, isUserAuthorized } from './features/Auth/Thunks';

function App(): ReactElement {
  const { authenticated, inProgress, getUser, signinSilent } = useAuthentication();

  configureGeneratedClient(getUser, signinSilent);

  const { currentTenant } = useAppSelector((state) => state.Tenants);
  const { messageErrors, notificationAlertMessage, userRole, activeRequestsCount } =
    useAppSelector((state) => state.appState);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const handleCloseErrors = (): void => {
    dispatch(setMessageErrors([]));
  };

  const handleCloseNotification = (): void => {
    dispatch(setNotificationAlertMessage(undefined));
  };

  useEffect(() => {
    configureGeneratedClient(getUser, signinSilent);
  }, [getUser, signinSilent]);

  // Safety net: promise rejections that no call site handled must never
  // crash the app - surface them in the error snackbar instead.
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
      event.preventDefault();
      const reason = event.reason as { message?: string } | string | undefined;
      const message =
        (typeof reason === 'string' ? reason : reason?.message) || 'An unexpected error occurred';
      dispatch(setMessageErrors([message]));
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }, [dispatch]);

  useEffect(() => {
    const unregister = history.listen(() => {
      buildTitle(history.location.pathname, currentTenant?.name);
    });
    buildTitle(history.location.pathname, currentTenant?.name);
    return () => unregister();
  }, [history, currentTenant]);

  useEffect(() => {
    if (authenticated && !inProgress && userRole === undefined) {
      dispatch(fetchCurrentUserRole());
    } else if (!authenticated && !inProgress && userRole !== undefined) {
      dispatch(setUserRole(undefined));
    }
  }, [authenticated, inProgress, dispatch, userRole]);

  const renderProtectedContent = (): ReactElement => {
    if (userRole === undefined) {
      return <AuthenticationInProgress />;
    }
    if (!isUserAuthorized(userRole)) {
      return <Forbidden />;
    }
    return <Home />;
  };

  return (
    <ErrorBoundary>
      <Switch>
        <Route path="/signin-oidc">
          <SignInOidcFragment />
        </Route>
        <Route path="/signin-oidc-popup">
          <SignInOidcFragment isPopup />
        </Route>
        <Route path="/signout-oidc">
          <SignOutOidcFragment />
        </Route>
        <Route path="/signout-oidc-popup">
          <SignOutOidcFragment isPopup />
        </Route>
        <Route path="/callback">
          <SignInOidcFragment />
        </Route>
        <Route path="/forget">
          <ForgetPassword />
        </Route>
        <Route path="/">
          <ProtectedRoute>{renderProtectedContent()}</ProtectedRoute>
        </Route>
      </Switch>
      <Backdrop
        open={activeRequestsCount > 0}
        sx={{
          color: '#fff',
          zIndex: (theme) => Math.max(theme.zIndex.drawer, 1300) + 100,
          backgroundColor: 'rgba(19, 34, 47, 0.4)',
          backdropFilter: 'blur(3px)',
          transition: 'all 0.25s ease-in-out',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 160,
            height: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          }}
        >
          <LoadingComponent />
        </Box>
      </Backdrop>
      <Snackbar
        open={messageErrors.length > 0}
        autoHideDuration={6000}
        onClose={handleCloseErrors}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseErrors} severity="error" sx={{ width: '100%' }}>
          {messageErrors.join(', ')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(notificationAlertMessage)}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
          {notificationAlertMessage}
        </Alert>
      </Snackbar>
    </ErrorBoundary>
  );
}

export default App;
